import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/ca-emissions.js

/**
 * EmissionsHandler
 * 
 * Manages emission results data and calculation operations for the application.
 * Responsible for:
 * - API interaction with Strapi emission result endpoints
 * - Real-time updates via WebSocket connection
 * - Calculation triggering and status management
 * - Data transformation and state management
 * - Recalculation flagging logic
 */
export class EmissionsHandler {
  /**
   * Initialize the emissions handler with store reference
   * @param {Object} store - Application store instance
   */
  constructor(store) {
    this.store = store;

    // Track recently updated activity IDs to avoid overwriting them
    this.recentlyUpdatedActivities = new Set();

    // ✅ Operation Lightsaber: Prevent duplicate loads during init
    this._loadingPromise = null;

    // Track which year is currently being calculated to prevent re-flagging during calculation
    this._calculatingYear = null;

    // Track post-calculation work - prevents re-flagging until all post-calc operations complete
    this._postCalculationInProgress = null; // Year string when post-calc work is happening

    // Register with WebSocketHandler if available
    if (this.store.webSocketHandler) {
      this.store.webSocketHandler.registerHandler(
        'EmissionsHandler',
        this.handleUpdate.bind(this),
        [
          'emissions',
          'emission-results',
          'emission-result:update', // ✅ FIX: Backend sends singular 'result'
          'calculation',
          'calculation:complete',
          'calculation:start',
          'calculation:error',
          'emission:update',
          'emission-results:update'
        ],
        this
      );
    }
  }

  /**
   * Load emission results for current project from the API
   * @returns {Promise<Object>} Emission results grouped by year
   * @throws {Error} If loading fails or no project ID is available
   */
  async loadAllEmissionResults() {
    // Skip in SSR/build environment
    if (typeof window === 'undefined') {
      return {};
    }

    // ✅ Operation Lightsaber: Prevent duplicate concurrent loads
    // If already loading, return the existing promise instead of starting a new load
    if (this._loadingPromise) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Emissions] Load already in progress, reusing existing promise');
      }
      return this._loadingPromise;
    }

    // Store the promise so concurrent calls can wait on it
    this._loadingPromise = this._doLoadAllEmissionResults();

    try {
      return await this._loadingPromise;
    } finally {
      this._loadingPromise = null;
    }
  }

  /**
   * Internal method that performs the actual load
   * @private
   */
  async _doLoadAllEmissionResults() {
    try {
      // Set loading state
      this.store.state.loading.emissionResults = true;
      this.store.notifySubscribers('loading');

      const projectId = this.store.getProjectId();

      // Validate projectId
      if (!projectId) {
        throw new Error('No project ID available - user not authenticated');
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Loading emission results for project:', projectId);
      }

      const startTime = Date.now();

      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-emission-results/project/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to fetch emission results: ${response.status} ${response.statusText} ${errorText}`);
      }

      const { data } = await response.json();

      if (process.env.NODE_ENV === 'development') {
        console.log(`Received ${data.length} emission results from API`);
      }

      // Transform into year-based structure with proper type handling
      const byYear = {};

      data.forEach(item => {
        const year = item.Year?.toString();
        if (!year) return;

        if (!byYear[year]) {
          byYear[year] = [];
        }

        byYear[year].push({
          id: item.id,
          year,
          scope: item.Scope || '',
          scopeDetails: item.ScopeDetails || '',
          businessUnit: item.BusinessUnit || '',
          countryCode: item.CountryCode || '',
          countryName: item.CountryName || '',
          scope3SectorCode: item.Scope3SectorCode || '',
          scope3SectorName: item.Scope3SectorName || '',
          supplierIdFrontend: item.SupplierIdFrontend || '',
          supplierName: item.SupplierName || '',
          supplierDominantHotspotSectorId: item.SupplierDominantHotspotSectorId || '',
          supplierDominantHotspotSectorName: item.SupplierDominantHotspotSectorName || '',
          supplierLinkingStatus: item.SupplierLinkingStatus || '',
          // Convert string numeric fields to numbers with fallbacks
          supplierLinkingPerformance: parseFloat(item.SupplierLinkingPerformance || '0') || 0,
          supplierLinkingBusinessSegmentSector: item.SupplierLinkingBusinessSegmentSector || '',
          supplierLinkingBusinessSegmentCountry: item.SupplierLinkingBusinessSegmentCountry || '',
          RemainingEmissionsTonsCO2eToday: parseFloat(item.RemainingEmissionsTonsCO2eToday || '0') || 0,
          SavedEmissionsTonsCO2eToday: parseFloat(item.SavedEmissionsTonsCO2eToday || '0') || 0,
          // Store the recalculation flag for UI decisions
          needsRecalculation: !!item.needsRecalculation,
          // Preserve server timestamps for debugging purposes only
          updatedAt: item.updatedAt || new Date().toISOString(),
          createdAt: item.createdAt || new Date().toISOString()
        });
      });

      if (process.env.NODE_ENV === 'development') {
        const duration = Date.now() - startTime;
        console.log(`Processed ${data.length} emission results in ${duration}ms`);
      }

      // Update store state with metadata
      this.store.state.data.emissionResults = {
        byYear,
        meta: {
          lastSync: new Date().toISOString(),
          totalCount: data.length,
          // Preserve these for UI feedback only
          lastCalculatedYear: this.store.state.data.emissionResults?.meta?.lastCalculatedYear,
          lastCalculationTime: this.store.state.data.emissionResults?.meta?.lastCalculationTime
        }
      };

      // Clear loading state
      this.store.state.loading.emissionResults = false;
      this.store.notifySubscribers('loading');

      await this.store.saveState();
      this.store.notifySubscribers('emissionResults');

      return byYear;

    } catch (error) {
      console.error('Failed to load emission results:', error);
      this.store.handleError('emissionResults', error);
      // Clear loading state on error
      this.store.state.loading.emissionResults = false;
      this.store.notifySubscribers('loading');
      throw error;
    }
  }

  /**
   * Mark a specific year's data as needing recalculation in the UI
   * @param {string|number} year - The year to flag
   * @returns {Promise<boolean>} Success status
   */
  async flagYearForRecalculation(year) {
    try {
      const yearStr = year?.toString();

      // Skip flagging if we're currently calculating this year
      // This prevents the recalculation loop where WebSocket events during calculation
      // would re-flag the year immediately after we just calculated it
      if (this._calculatingYear === yearStr) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Skipping flag for year ${year} - calculation in progress`);
        }
        return true;
      }

      // Skip flagging if post-calculation work is in progress for this year
      // This prevents React re-renders from triggering re-flag during business segments refresh, etc.
      if (this._postCalculationInProgress === yearStr) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Skipping flag for year ${year} - post-calculation work in progress`);
        }
        return true;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`Flagging year ${year} for recalculation`);
      }

      // Directly update the state
      if (this.store.state.data.emissionResults?.byYear?.[yearStr]) {
        // Mark all emission results for this year as needing recalculation
        this.store.state.data.emissionResults.byYear[yearStr] =
          this.store.state.data.emissionResults.byYear[yearStr].map(item => ({
            ...item,
            needsRecalculation: true
          }));

        // Notify subscribers to update UI
        this.store.notifySubscribers('emissionResults');
      }

      return true;
    } catch (error) {
      console.error(`Error in flagYearForRecalculation: ${error.message}`);
      return true; // Always return true to avoid breaking the workflow
    }
  }

  /**
   * Signal that post-calculation work (business segments refresh, etc.) is complete
   * Call this after all post-calculation operations finish to allow change detection to resume
   * @param {string|number} year - The year that finished post-calculation work
   */
  endPostCalculationWork(year) {
    const yearStr = year?.toString();
    if (this._postCalculationInProgress === yearStr) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Post-calculation work completed for year ${year}`);
      }
      this._postCalculationInProgress = null;
    }
  }

  /**
   * Trigger calculation process for specified project and year
   * Handles streaming response with heartbeat from backend
   * @param {Object} params - Calculation parameters
   * @param {string|number} params.projectId - Project ID 
   * @param {string|number} params.year - Year to calculate
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If calculation fails or parameters are invalid
   */
  async calculate({ projectId, year }) {
    try {
      // Validate required parameters
      if (!projectId || !year) {
        throw new Error('Both projectId and year are required for calculation');
      }

      // Ensure numeric types for API
      const parsedProjectId = parseInt(projectId);
      const parsedYear = parseInt(year);

      if (isNaN(parsedProjectId) || isNaN(parsedYear)) {
        throw new Error('Invalid projectId or year');
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`🚀 Starting emission calculation for project ${parsedProjectId}, year ${parsedYear}...`);
      }

      // Set flag to prevent re-flagging during calculation
      this._calculatingYear = parsedYear.toString();

      const startTime = Date.now();

      // Update calculation status for UI feedback
      this.store.notifySubscribers('calculationStatus', {
        type: 'loading',
        message: `Calculating emissions for year ${parsedYear}... This may take 2-3 minutes.`,
        timestamp: new Date(),
        year: parsedYear
      });

      // Make POST request to Strapi - NO TIMEOUT, backend handles heartbeat
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-emission-results/project/${parsedProjectId}/calculate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({ year: parsedYear })
          // CRITICAL: No timeout option - let backend heartbeat handle it
        }
      );

      // Read the full response (will contain heartbeat spaces + final JSON)
      const text = await response.text();

      // Extract the JSON from the streaming response (ignore heartbeat spaces)
      // The heartbeat sends space characters before the final JSON, so we find the FIRST '{'
      const trimmedText = text.trim();
      const firstJsonStart = trimmedText.indexOf('{');

      if (firstJsonStart === -1) {
        throw new Error('Invalid response format - no JSON found');
      }

      const jsonResponse = trimmedText.substring(firstJsonStart);
      let data;

      try {
        data = JSON.parse(jsonResponse);
      } catch (parseError) {
        console.error('JSON parse error:', parseError, 'Response:', jsonResponse);
        throw new Error('Invalid JSON response from server');
      }

      if (!data.success) {
        throw new Error(data.error || data.message || 'Calculation failed');
      }

      const duration = Date.now() - startTime;

      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Emission calculation completed successfully in ${duration}ms`);
        console.log('[CA] Response data:', data);
        console.log('[CA] linkingIds in response:', data.linkingIds);
      }

      // After successful calculation - reload all related data
      // CRITICAL: Add delay to ensure Snowflake view is updated
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Clear any existing errors before reload to detect new failures
      this.store.handleError('supplierInput', null);
      this.store.handleError('spendDistribution', null);
      this.store.handleError('emissionResults', null);

      // Ensure all dependent data is loaded
      await Promise.all([
        this.store.supplierInputHandler.loadSupplierInput(),
        this.store.spendDistributionHandler.loadSpendDistribution()
      ]);

      // Now reload emission results
      await this.loadAllEmissionResults();

      // Check if any load operations recorded errors in the store
      const loadErrors = [];
      if (this.store.state.errors.supplierInput) {
        loadErrors.push('supplier input');
      }
      if (this.store.state.errors.spendDistribution) {
        loadErrors.push('spend distribution');
      }
      if (this.store.state.errors.emissionResults) {
        loadErrors.push('emission results');
      }

      // If any loads failed, throw an error so the UI can inform the user
      if (loadErrors.length > 0) {
        throw new Error(`Calculation completed but failed to load updated ${loadErrors.join(', ')}. Please refresh the page.`);
      }

      // Track last calculated year for UI feedback
      if (!this.store.state.data.emissionResults.meta) {
        this.store.state.data.emissionResults.meta = {};
      }

      this.store.state.data.emissionResults.meta.lastCalculatedYear = parsedYear.toString();
      this.store.state.data.emissionResults.meta.lastCalculationTime = new Date().toISOString();

      // Save to localStorage to persist
      await this.store.saveState();

      // Notify listeners of successful calculation
      this.store.notifySubscribers('calculationStatus', {
        type: 'success',
        message: `Calculation for year ${parsedYear} completed successfully`,
        timestamp: new Date(),
        duration,
        year: parsedYear
      });

      // Also notify about the emission results update to trigger UI refresh
      this.store.notifySubscribers('emissionResults', this.store.state.data.emissionResults);

      // Clean up any orphaned data
      if (this.store.supplierInputHandler?.cleanupOrphanedSupplierInputs) {
        await this.store.supplierInputHandler.cleanupOrphanedSupplierInputs();
      }

      // Operation Lightsaber: Targeted supplier update using linkingIds from HTTP response
      // Backend already synced these suppliers to Strapi before sending the response
      // We use the HTTP response (not WebSocket) because this is OUR calculation
      if (this.store.supplychainSuppliersHandler && data.linkingIds?.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[CA] Updating ${data.linkingIds.length} suppliers via targeted update`);
        }
        this.store.supplychainSuppliersHandler.fetchAndUpdateByLinkingIds(data.linkingIds)
          .catch(err => {
            console.warn('[CA] Targeted supplier update failed:', err);
            // Fall back to marking stale - will reload on next SCM page visit
            this.store.supplychainSuppliersHandler.markStale('CA calculation completed');
          });
      }

      // Clear the calculating flag but start post-calculation protection
      // The UI layer must call endPostCalculationWork() when all post-calc operations complete
      this._calculatingYear = null;
      this._postCalculationInProgress = parsedYear.toString();

      return true;

    } catch (error) {
      // Clear the calculating flags on error too
      this._calculatingYear = null;
      this._postCalculationInProgress = null;

      console.error('❌ Emission calculation failed:', error);

      // Notify listeners of calculation failure
      this.store.notifySubscribers('calculationStatus', {
        type: 'error',
        message: `Calculation failed: ${error.message}`,
        timestamp: new Date(),
        year
      });

      if (this.store.handleError) {
        this.store.handleError('emissionResults', error);
      }

      throw error;
    }
  }

  /**
   * Process real-time updates received via WebSocket connection
   * @param {Object} data - Update data from WebSocket
   * @returns {Promise<void>}
   */
  async handleUpdate(data) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Emission results update received:', data);
    }

    try {
      // Extract year from data for notification purposes
      const updateYear = data.year || data.Year;

      // NOTE: Supplier updates for calculation events are handled by
      // websocket/index.js using targeted updates (Operation Lightsaber)

      // Reload emission results
      await this.loadAllEmissionResults();

      // Notify UI with appropriate message
      if (updateYear) {
        this.store.notifySubscribers('syncStatus', {
          type: 'success',
          message: `Emission results updated for year ${updateYear}`,
          timestamp: new Date(),
          details: data
        });
      } else {
        this.store.notifySubscribers('syncStatus', {
          type: 'success',
          message: 'Emission results updated',
          timestamp: new Date(),
          details: data
        });
      }

      // Explicitly notify UI to refresh with current data
      this.store.notifySubscribers('emissionResults', this.store.state.data.emissionResults);

    } catch (error) {
      console.error('WebSocket update handler failed:', error);
      this.store.handleError('emissionResults', error);

      // Still notify subscribers even on error
      this.store.notifySubscribers('emissionResults', this.store.state.data.emissionResults);
    }
  }

  /**
   * Retrieve emission results for a specific year
   * @param {string|number} year - Year to get results for
   * @returns {Array|null} Array of emission result objects or null if none found
   */
  getEmissionResults(year) {
    return this.store.state.data.emissionResults?.byYear?.[year] || null;
  }

  /**
   * Check if emission results exist for a specific year
   * @param {string|number} year - Year to check
   * @returns {boolean} True if results exist for the year
   */
  hasEmissionResults(year) {
    return !!this.store.state.data.emissionResults?.byYear?.[year];
  }

  /**
   * Get all years that have emission results
   * @returns {Array<string>} Array of years sorted in descending order
   */
  getAvailableYears() {
    return Object.keys(this.store.state.data.emissionResults?.byYear || {})
      .sort((a, b) => b.localeCompare(a));
  }

  /**
   * Calculate emission totals grouped by scope for a specific year
   * @param {string|number} year - Year to calculate for
   * @returns {Object|null} Object with scope keys and emission totals, or null if no data
   */
  getTotalsByScopeForYear(year) {
    const results = this.getEmissionResults(year);
    if (!results) return null;

    return results.reduce((totals, item) => {
      const scope = item.scope || 'Unknown';
      const emissions = item.RemainingEmissionsTonsCO2eToday || 0;

      // FIX: Only add market-based Scope 2 to totals
      // Skip location-based Scope 2 entirely
      if (scope === 'Scope 2 (location-based)') {
        // Don't add location-based to any totals
        return totals;
      }

      // Add all other scopes normally
      if (!totals[scope]) {
        totals[scope] = 0;
      }
      totals[scope] += emissions;

      return totals;
    }, {});
  }

  /**
   * Determine if a year's data needs recalculation
   * @param {string|number} year - Year to check
   * @returns {boolean} True if recalculation is needed
   */
  needsRecalculation(year) {
    const yearStr = year?.toString();
    if (!yearStr) return false;

    // Get emission results for this year
    const results = this.getEmissionResults(yearStr);

    // If no results but we have data, we need calculation
    if (!results || results.length === 0) {
      return this.hasDataForYear(yearStr);
    }

    // Check if any results have the needsRecalculation flag set
    return results.some(r => r.needsRecalculation === true);
  }

  /**
   * Check if there is any data available for a specific year
   * Used to determine if calculation is possible for years with no existing results
   * @param {string} year - Year to check
   * @returns {boolean} True if any relevant data exists for the year
   */
  hasDataForYear(year) {
    if (!year) return false;

    // Check all relevant data types
    const hasActivities =
      (this.store.state.data.activityData?.byYear?.[year]?.scope1 || []).length > 0 ||
      (this.store.state.data.activityData?.byYear?.[year]?.scope2 || []).length > 0;

    const hasSpendData = Array.isArray(this.store.state.data.spendVolume?.byYear?.[year]) &&
      this.store.state.data.spendVolume.byYear[year].length > 0;

    const hasDistributions = Array.isArray(this.store.state.data.spendDistribution?.byYear?.[year]) &&
      this.store.state.data.spendDistribution.byYear[year].length > 0;

    const hasSuppliers = Array.isArray(this.store.state.data.supplierInput?.byYear?.[year]) &&
      this.store.state.data.supplierInput.byYear[year].length > 0;

    return hasActivities || hasSpendData || hasDistributions || hasSuppliers;
  }
}
